import React from "react";
import LocationSearchCard from "./LocationSearchCard";
import { RecentLocation } from "./searchStorage";
import { AirportOption } from "./useAirportCodes";

interface AirportFieldProps {
  type: "from" | "to";
  label: string; // "Flying From ?" / "Going to ?"
  selectedCode: string;
  displayValue: string;
  options: AirportOption[];
  recentLocations: RecentLocation[];
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  searchInput: string;
  setSearchInput: (value: string) => void;
  isLoading: boolean;
  onSearch: (input: string) => Promise<void> | void;
  onSelect: (code: string, name: string) => void;
  onClear: () => void;
  onClose: () => void;
  error?: string;
}

// A single Flying-From / Going-to field: the read-only display input + clear
// button, with the airport search dropdown anchored to it.
const AirportField: React.FC<AirportFieldProps> = ({
  type,
  label,
  selectedCode,
  displayValue,
  options,
  recentLocations,
  isOpen,
  setOpen,
  searchInput,
  setSearchInput,
  isLoading,
  onSearch,
  onSelect,
  onClear,
  onClose,
  error,
}) => (
  // The whole padded box is the click target (not just the input), so clicking
  // the label or empty space opens the picker too. Guard against reopening when
  // the dropdown is already open (e.g. a click that selects an option and closes
  // it would otherwise bubble up and reopen the dropdown).
  <div
    className="relative flex-1 p-3 cursor-pointer rounded-lg transition-colors hover:bg-gray-50"
    onClick={() => {
      if (!isOpen) setOpen(true);
    }}
  >
    <label className="flex items-center gap-1.5 mb-1 text-xs text-[#6B7280] cursor-pointer">
      <i className="st-flight text-[#6B7280] text-[11px]"></i>
      {label}
    </label>
    <div className="relative">
      <input
        type="text"
        readOnly
        value={displayValue}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        placeholder="Search by Place"
        className="w-full py-1.5 pr-6 border-none bg-transparent text-sm text-[#1F2937] font-medium cursor-pointer focus:outline-none placeholder-gray-400 truncate"
      />
      {selectedCode ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <i className="st-close text-sm"></i>
        </button>
      ) : (
        // Caret signals the field opens a picker (not a plain text input).
        <i className="st-arrow-down text-[10px] text-gray-400 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none"></i>
      )}

      <LocationSearchCard
        type={type}
        value={selectedCode}
        onChange={onSelect}
        recentLocations={recentLocations}
        isOpen={isOpen}
        onOpen={() => setOpen(true)}
        onClose={onClose}
        placeholder="Search airports..."
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        options={options}
        isLoading={isLoading}
        label={type === "from" ? "From" : "To"}
        onSearch={onSearch}
      />
    </div>
    {error && <p className="text-red-500 text-[11px] mt-0.5">{error}</p>}
  </div>
);

export default AirportField;
